import React, { Fragment } from 'react'
import classNames from 'classnames'
import { graphql, StaticQuery } from 'gatsby'

import Layout from './layout'
import Footer from './footer'
import { H0, H1, H5 } from './typography'
import Link, { Button } from './link'
import Prodigy from './prodigy'
import Slider from './slider'
import Carousel from './carousel'
import { Offer, OfferBanner } from './offer'
import { Tweet } from './embed'
import Logo from '../images/logo.svg'
import Blob from '../images/blob.svg'
import BlobTopLeft from '../images/blob_top-left.svg'
import BlobRight from '../images/blob_right.svg'
import * as classes from '../styles/landing.module.sass'

// Ecosystem
import SpacyLogo from '../images/logos/spacy.svg'
import PyTorchLogo from '../images/logos/pytorch.svg'
import TensorFlowLogo from '../images/logos/tensorflow.svg'
import JupyterLogo from '../images/logos/jupyter.svg'
// Users
import BayerLogo from '../images/logos/bayer.svg'
import AllenAILogo from '../images/logos/allenai.svg'
import KPMGLogo from '../images/logos/kpmg.svg'
import BaringsLogo from '../images/logos/barings.svg'
import ChorusLogo from '../images/logos/chorus.svg'
import BASFLogo from '../images/logos/basf.svg'
import MicrosoftLogo from '../images/logos/microsoft.svg'
import MunichReLogo from '../images/logos/munichre.svg'
import APLogo from '../images/logos/ap.svg'

// Features
import NerFeatureIcon from '../images/icons/landing-ner.svg'
import SpancatFeatureIcon from '../images/icons/landing-spancat.svg'
import CVFeatureIcon from '../images/icons/landing-cv.svg'
import AudioFeatureIcon from '../images/icons/landing-audio.svg'
import EvalFeatureIcon from '../images/icons/landing-eval.svg'
import TaskRoutingIcon from '../images/icons/landing-routing.svg'
import TextcatFeatureIcon from '../images/icons/landing-textcat.svg'
import RelationsFeatureIcon from '../images/icons/landing-rel.svg'
import ZeroShotFeatureIcon from '../images/icons/landing-zeroshot.svg'
import ModelTrainingFeatureIcon from '../images/icons/landing-training.svg'
import TournamentsFeatureIcon from '../images/icons/landing-tournament.svg'
import { getImage } from 'gatsby-plugin-image'

const FEATURES = [
    {
        text: ['Named Entity', 'Recognition'],
        url: '/features/named-entity-recognition',
        Icon: NerFeatureIcon,
    },
    {
        text: ['Span', 'Categorization'],
        url: '/features/span-categorization',
        Icon: SpancatFeatureIcon,
    },
    {
        text: ['Text', 'Classification'],
        url: '/features/text-classification',
        Icon: TextcatFeatureIcon,
    },
    {
        text: ['Dependencies &', 'Relations'],
        url: '/features/dependencies-relations',
        Icon: RelationsFeatureIcon,
    },
    { text: ['Computer', 'Vision'], url: '/features/computer-vision', Icon: CVFeatureIcon },
    { text: ['Audio &', 'Video'], url: '/features/audio-video', Icon: AudioFeatureIcon },
    { text: ['A/B', 'Evaluation'], url: '/features/ab-evaluation', Icon: EvalFeatureIcon },
    { text: ['Task', 'Routing'], url: '/features/task-routing', Icon: TaskRoutingIcon },
    {
        text: ['Prompt', 'Engineering'],
        url: '/features/prompt-engineering',
        Icon: TournamentsFeatureIcon,
    },
    {
        text: ['Large Language', 'Models'],
        url: '/features/large-language-models',
        Icon: ZeroShotFeatureIcon,
    },
    {
        text: ['Model', 'Training'],
        url: '/features/training',
        Icon: ModelTrainingFeatureIcon,
    },
]

const LOGOS = {
    spacy: SpacyLogo,
    pytorch: PyTorchLogo,
    tensorflow: TensorFlowLogo,
    jupyter: JupyterLogo,
    bayer: BayerLogo,
    allenai: AllenAILogo,
    kpmg: KPMGLogo,
    barings: BaringsLogo,
    chorus: ChorusLogo,
    basf: BASFLogo,
    microsoft: MicrosoftLogo,
    munichre: MunichReLogo,
    ap: APLogo,
}

export const UserLogos = ({ title = 'Prodigy users include' }) => {
    return (
        <Banner title={title}>
            <LogoRow
                logos={[
                    { name: 'bayer', width: 50 },
                    { name: 'kpmg', width: 90 },
                    { name: 'ap', width: 45 },
                    { name: 'munichre', width: 120 },
                    { name: 'microsoft', width: 120 },
                    { name: 'allenai', width: 100 },
                    { name: 'barings', width: 100 },
                    { name: 'basf', width: 95 },
                    { name: 'chorus', width: 100 },
                ]}
                className={classes.bannerLogos}
                spaced
            />
        </Banner>
    )
}

export const FeaturesRow = ({ title = 'Browse features ' }) => (
    <Banner title={title}>
        <ul className={classes.featuresRow}>
            {FEATURES.map(({ text, url, Icon }, i) => {
                return (
                    <li key={i}>
                        <Link to={url} hidden>
                            <span className={classes.featureIcon} aria-hidden="true">
                                {Icon && <Icon className={classes.featureIconSvg} />}
                            </span>

                            <H5 className={classes.featuresRowTitle}>
                                {text.map((t, iT) => (
                                    <Fragment key={iT}>
                                        {t !== 0 && <br />}
                                        {t}
                                    </Fragment>
                                ))}
                            </H5>
                        </Link>
                    </li>
                )
            })}
        </ul>
    </Banner>
)

export const Testimonials = ({ title = 'What others say' }) => {
    return (
        <StaticQuery
            query={graphql`
                query {
                    site {
                        siteMetadata {
                            navigation {
                                text
                                menu {
                                    main {
                                        text
                                        label
                                        url
                                    }
                                }
                            }
                        }
                    }
                    allFile(filter: { name: { regex: "/^testimonial_/" } }) {
                        edges {
                            node {
                                name
                                childImageSharp {
                                    gatsbyImageData(layout: FIXED, width: 36, height: 36)
                                }
                            }
                        }
                    }
                }
            `}
            render={data => {
                const files = data.allFile.edges.map(edge => ({
                    [edge.node.name]: getImage(edge.node),
                }))
                const testimonials = Object.assign({}, ...files)
                return (
                    <Banner title={title} className={classes.bannerTweets}>
                        <Carousel>
                            <Tweet
                                id="935217160732979201"
                                account="ahalterman"
                                name="Andy Halterman"
                                image={testimonials['testimonial_ahalterman']}
                            >
                                Mordecai would not have been possible without{' '}
                                <Link to="https://twitter.com/explosion_ai">@explosion_ai</Link>
                                &#39;s Prodigy. A lack of labeled data held geoparsing back for
                                years. It took a week to fix that with Prodigy.
                            </Tweet>
                            <Tweet
                                id="964466872883216384"
                                account="FullFact"
                                name="FullFact"
                                verified
                                image={testimonials['testimonial_fullfact']}
                            >
                                We&#39;ve collected 25,000+ annotations from 90 fantastic
                                volunteers, to support our automated factchecking work thanks to
                                Prodigy, an annotation tool created by{' '}
                                <Link to="https://twitter.com/explosion_ai">@explosion_ai</Link>.
                            </Tweet>
                            <Tweet
                                id="943139289520537600"
                                account="iamtrask"
                                name="Andrew Trask"
                                image={testimonials['testimonial_iamtrask']}
                            >
                                I&#39;m a huge fan of everything{' '}
                                <Link to="https://twitter.com/explosion_ai">@explosion_ai</Link>{' '}
                                does... they&#39;re brilliant... and their new annotation tool is
                                the best I&#39;ve ever seen.
                            </Tweet>
                            <Tweet
                                id="1291846084592054274"
                                account="levidehaan"
                                name="Levi DeHaan"
                                image={testimonials['testimonial_levidehaan']}
                            >
                                ohhh snap I might have convinced my company to buy a bunch of
                                licenses for Prodigy by{' '}
                                <Link to="https://twitter.com/explosion_ai">@explosion_ai</Link>{' '}
                                woot! :D 🎵 I'm gonna train some models, I'm gonna train some models
                                🎵 I'm going to have some fun :D
                            </Tweet>
                            <Tweet
                                id="1211961862717394944"
                                account="cohenrap"
                                name="Raphael cohen"
                                image={testimonials['testimonial_cohenrap']}
                                width={350}
                            >
                                <Link to="https://prodi.gy">Prodi.gy</Link> is by far the best ROI
                                we had on any tool!
                            </Tweet>
                            <Tweet
                                id="962192007736291329"
                                account="oliverbeavers"
                                name="Oliver Beavers"
                                width={450}
                                image={testimonials['testimonial_oliverbeavers']}
                            >
                                just finishing up first major{' '}
                                <Link to="https://twitter.com/hashtag/nlp">#nlp</Link> project with{' '}
                                <Link to="https://twitter.com/explosion_ai">@explosion_ai</Link>
                                &#39;s prodigy active learning platform. in 3 hours, did what took
                                &gt; 10 volunteers, painful google sheets nonsense, and weeks worth
                                of time. game changer.{' '}
                                <Link to="https://twitter.com/hashtag/yesimshilling">
                                    #yesimshilling
                                </Link>
                            </Tweet>
                            <Tweet
                                id="999664630006996992"
                                name="David Campion"
                                account="Orbis_21"
                                image={testimonials['testimonial_Orbis_21']}
                            >
                                “Text Classification: Be lazy, use{' '}
                                <Link to="https://prodi.gy">Prodi.gy</Link> (a tool by{' '}
                                <Link to="https://twitter.com/explosion_ai">@explosion_ai</Link>)
                                !”. This tool (<Link to="https://prodi.gy">prodi.gy</Link>) is
                                fantastic and really help us to speed-up and build our models.
                            </Tweet>
                            <Tweet
                                id="1038185889548001281"
                                name="Ajinkya Kale"
                                account="ajinkyakale"
                                image={testimonials['testimonial_ajinkyakale']}
                            >
                                Its amazing, every time i try to build something in house these guys
                                beat me at it providing an awesome solution out of the box!
                            </Tweet>
                        </Carousel>
                    </Banner>
                )
            }}
        />
    )
}

export const FeatureLanding = ({
    title,
    description,
    callToAction,
    callToActionText = 'View the documentation',
    logo = false,
    large = false,
    blobs = true,
    blobColor1 = null,
    blobColor2 = null,
    icon = null,
    children,
}) => {
    const IconComponent = icon
    return (
        <Layout navClassName={classes.nav}>
            <Header
                title={title}
                logo={logo}
                large={large}
                className={classes.featureHeader}
                style={{ '--blob-color1': blobColor1, '--blob-color2': blobColor2 }}
            >
                {icon && (
                    <span className={classes.featureIcon} aria-hidden="true">
                        <IconComponent className={classes.featureIconSvg} />
                    </span>
                )}
                {blobs && (
                    <>
                        <BlobTopLeft className={classes.blobTopLeft} width={0} />
                        <BlobRight className={classes.blobRight} width={0} />
                    </>
                )}
                {description}
            </Header>

            {children}

            {callToAction && (
                <section className={classes.callToAction}>
                    <Button to={callToAction} primary large>
                        {callToActionText}
                    </Button>
                </section>
            )}

            <Footer />
        </Layout>
    )
}

export const Section = ({
    dark = false,
    last = false,
    center = false,
    vcenter = false,
    narrow = false,
    gutterBottom = true,
    columns,
    className,
    children,
}) => {
    const sectionClassNames = classNames(classes.section, className, {
        [classes.sectionDark]: !!dark,
        [classes.sectionLast]: !!last,
        [classes.sectionCenter]: !!center,
        [classes.sectionVcenter]: !!vcenter,
        [classes.sectionNarrow]: !!narrow,
        [classes.sectionNoGutter]: !gutterBottom,
    })
    function getColumns(columns) {
        const a = Number.isInteger(columns[0]) ? `${columns[0]}%` : columns[0]
        const b = Number.isInteger(columns[1]) ? `${columns[1]}%` : columns[1]
        return `${a} ${b}`
    }

    const gridTemplateColumns = columns ? getColumns(columns) : undefined
    return (
        <section className={sectionClassNames} style={{ gridTemplateColumns }}>
            {children}
        </section>
    )
}

export const FeatureSection = props => <Section narrow vcenter {...props} />

export const Column = ({ title, className, children, width, spaced, style = {} }) => (
    <div
        className={classNames(className, { [classes.columnSpaced]: spaced })}
        style={{ width, ...style }}
    >
        {title && <H1 Component="h2">{title}</H1>}
        {children}
    </div>
)

export const Caption = ({ children }) => <p className={classes.caption}>{children}</p>

export const Header = ({ title, logo = true, large = false, children, className, style }) => (
    <header
        className={classNames(classes.header, classes.sectionDark, classes.sectionLast, className, {
            [classes.sectionLarge]: !!large,
        })}
        style={style}
    >
        {large && <Blob className={classes.blob} width={0} />}
        <Link to="/" hidden className={classNames({ [classes.logoTop]: !logo })}>
            <Logo width={100} height={29} className={classes.logo} />
        </Link>
        <H0 className={classNames({ [classes.headerTitleLarge]: !!large })}>{title}</H0>
        <p className={classNames(classes.headerDesc, { [classes.headerDescLarge]: !!large })}>
            {children}
        </p>
    </header>
)

export const ProdigySlider = ({
    id = 'uiExamples',
    data = [],
    showButtons = true,
    center = true,
    ...props
}) => {
    const uiExamples = data.map(({ name, title, viewId, content, config = {} }) => ({
        name: name,
        content: (
            <Prodigy
                key={name}
                title={title}
                view={viewId}
                content={JSON.stringify(content)}
                config={JSON.stringify(config)}
                showButtons={showButtons}
                center={center}
            />
        ),
    }))
    return <Slider id={id} items={uiExamples} {...props} />
}

export const LogoRow = ({
    logos,
    defaultHeight = 35,
    grayscale = true,
    spaced = false,
    className,
}) => (
    <ul className={classNames(classes.logoRow, className)}>
        {logos.map(({ name, url, width }, i) => {
            const Component = LOGOS[name]
            const defaults = Component.defaultProps
            const newHeight = width ? (defaults.height / defaults.width) * width : defaultHeight
            const newWidth = width || (defaults.width / defaults.height) * defaultHeight
            const logoClassNames = classNames({ [classes.logoGrayscale]: grayscale })
            const itemClassNames = classNames(classes.logoRowItem, {
                [classes.logoRowItemSpaced]: !!spaced,
            })
            const logo = (
                <Component width={newWidth} height={newHeight} className={logoClassNames} />
            )
            return (
                <li className={itemClassNames} key={i}>
                    {url ? (
                        <Link to={url} aria-label={name} hidden>
                            {logo}
                        </Link>
                    ) : (
                        logo
                    )}
                </li>
            )
        })}
    </ul>
)

export const Banner = ({ title, className, children }) => (
    <section className={classNames(classes.banner, className)}>
        {title && <h4 className={classes.bannerTitle}>{title}</h4>}
        {children}
    </section>
)

export const PricingTable = ({ licenses, academic }) => {
    return (
        <>
            <section className={classes.pricing}>
                {licenses.map((item, i) => (
                    <Offer key={i} {...item} />
                ))}
                <OfferBanner {...academic} className={classes.pricingBanner} />
            </section>
        </>
    )
}
